import { logger } from '@tunasong/models'
import { useCommandSpecRunner } from '@tunasong/plugin-lib'
import type { Entity, EntityCommandSpec, Persisted } from '@tunasong/schemas'
import { useEffect, useRef } from 'react'
import { useEventCommands } from '../../commands/event-commands.hook.js'
import { useUserConfig } from '../../config/config-service.hook.js'

type CommandSpecRunner = NonNullable<ReturnType<typeof useCommandSpecRunner>>

/**
 * The effect below floats each command, so a failure must end here. The runner's loads `unwrap()`, and a command
 * whose entity can't be read (a 403, or a WAF block answering HTML) escaped as an unhandled rejection (GH #1731, W9).
 */
const runLogged = (runner: CommandSpecRunner, spec: EntityCommandSpec, targetEntityId: string) =>
  void runner({ spec, targetEntityId }).catch(error =>
    logger.warn(`Event command ${spec.commandId} failed for ${targetEntityId}`, error)
  )

/**
 * Run the command on mount, or when any of the parameters change.
 * This hook will run once.
 */
export const useRunEventCommands = ({ entity }: { entity?: Persisted<Entity> }) => {
  const [maturity] = useUserConfig('featureMaturity')

  const eventCommands = useEventCommands(entity)

  const runner = useCommandSpecRunner()
  const runCounter = useRef(0)

  useEffect(() => {
    if (!runner || !entity?.id || runCounter.current > 0) {
      // runner is not ready
      return
    }
    runCounter.current += 1
    logger.debug(`Running Enter event commands, count: ${runCounter.current}`)
    const onEnterCommandSpecs = eventCommands.onEnter ?? []
    const onLeaveCommandSpecs = eventCommands.onLeave ?? []

    for (const spec of onEnterCommandSpecs) {
      runLogged(runner, spec, entity.id)
    }

    return () => {
      logger.debug(`Running Leave event commands, count: ${runCounter.current}`)
      for (const leaveCommand of onLeaveCommandSpecs) {
        runLogged(runner, leaveCommand, entity.id)
      }
    }
  }, [maturity, entity?.id, runner, eventCommands.onEnter, eventCommands.onLeave])
}
